/**
 * Sortable table columns (Bill, 2026-10-26: "where columns can and should be sortable").
 * Click a header to sort; click again to reverse. Each table remembers its last sort in this
 * browser. Blank values always go last. Numbers and dates sort as numbers and dates.
 */
import { useMemo, useState } from "react";

type Val = string | number | null | undefined;
export interface SortState { key: string; dir: "asc" | "desc" }

const read = (id: string): SortState | null => {
  try { const v = localStorage.getItem(`docvault.sort.${id}`); return v ? (JSON.parse(v) as SortState) : null; } catch { return null; }
};
const write = (id: string, s: SortState) => { try { localStorage.setItem(`docvault.sort.${id}`, JSON.stringify(s)); } catch { /* private mode */ } };

const cmp = (a: Val, b: Val) => {
  const ea = a === null || a === undefined || a === "", eb = b === null || b === undefined || b === "";
  if (ea || eb) return ea && eb ? 0 : ea ? 1 : -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
};

/** rows sorted by the chosen column, plus a header cell maker. */
export function useSort<T>(id: string, rows: T[], cols: Record<string, (r: T) => Val>, initial: SortState) {
  const [s, setS] = useState<SortState>(() => { const v = read(id); return v && cols[v.key] ? v : initial; });
  const sorted = useMemo(() => {
    const get = cols[s.key];
    if (!get) return rows;
    const out = [...rows].sort((x, y) => {
      const ax = get(x), ay = get(y);
      const blank = (v: Val) => v === null || v === undefined || v === "";
      if (blank(ax) || blank(ay)) return cmp(ax, ay); // blanks last either way
      return s.dir === "asc" ? cmp(ax, ay) : cmp(ay, ax);
    });
    return out;
  }, [rows, s.key, s.dir]);
  const set = (key: string) => setS((cur) => { const next: SortState = cur.key === key ? { key, dir: cur.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" }; write(id, next); return next; });
  /** A sortable header cell: th("name", "Name", "hide-md"). */
  const th = (k: string, label: React.ReactNode, className?: string) => {
    const on = s.key === k;
    return (
      <th key={k} className={className} aria-sort={on ? (s.dir === "asc" ? "ascending" : "descending") : "none"}>
        <button type="button" className={`th-sort ${on ? "on" : ""}`} onClick={() => set(k)} title="Sort">
          {label}<span className="th-arrow" aria-hidden="true">{on ? (s.dir === "asc" ? "▲" : "▼") : "↕"}</span>
        </button>
      </th>
    );
  };
  return { sorted, th, sort: s };
}

/** Day or timestamp text to a number for sorting. */
export const timeOf = (v: string | null | undefined) => { if (!v) return null; const t = Date.parse(v.includes("T") || v.includes(" ") ? v.replace(" ", "T") + (/[zZ]|[+-]\d\d:?\d\d$/.test(v) ? "" : "Z") : `${v}T12:00:00Z`); return Number.isNaN(t) ? null : t; };
/** IPv4 to a number so 10.0.0.9 sorts before 10.0.0.10. */
export const ipNum = (v: string | null | undefined) => { if (!v || !/^\d+\.\d+\.\d+\.\d+$/.test(v)) return v || null; return v.split(".").reduce((n, p) => n * 256 + Number(p), 0); };
